import { AxiosError, AxiosResponse } from 'axios'
import TextFieldWithUnit from 'common/components/TextFieldWithUnit'
import ThaiDatePicker from 'common/components/ThaiDatePicker'
import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import useYupValidationResolver from 'common/hooks/useYupValidationResolver'
import { formatDate } from 'common/utils/date'
import { errorMessageByField } from 'common/utils/hook-form'
import { numberSchema } from 'common/utils/yup'
import endOfToday from 'date-fns/endOfToday'
import parseISO from 'date-fns/parseISO'
import BaseRecordDialog from 'modules/pond/components/BaseRecordDialog'
import { useSpeciesName } from 'modules/pond/api/selector'
import { useMutateCreateWaterBacteria, useMutatePatchWaterVibrio } from 'modules/water/api/mutation'
import { Vibrio, WaterEditData } from 'modules/water/api/types'
import { waterDateFilterState } from 'modules/water/stores/atom'
import { normalizeSpeciesName } from 'utils/analytics'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'
import * as Yup from 'yup'

import { Box, Grid } from '@mui/material'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

interface BacteriaRecordDialogProps {
  onClose: () => void
  open: boolean
  isEdit?: boolean
  onSuccessSave?: () => void
  editData?: WaterEditData
}

interface FormDataType {
  date: Date
  greenVibrio: number | undefined
  yellowVibrio: number | undefined
}

const BacteriaRecordDialog = ({ onClose, open, isEdit, editData }: BacteriaRecordDialogProps) => {
  const { t } = useTranslation(['common', 'water'])

  const [submitting, setSubmitting] = useState(false)

  const date = useRecoilValue(waterDateFilterState)

  const vibrioEditData = editData as Vibrio | undefined

  const recordWaterBacteriaMutation = useMutateCreateWaterBacteria()
  const updateWaterBacteriaMutation = useMutatePatchWaterVibrio()
  const speciesName = useSpeciesName()
  const { trackEvent } = useAnalytics()

  const schema = Yup.object().shape({
    date: Yup.date(),
    greenVibrio: numberSchema(t('require.number')),
    yellowVibrio: numberSchema(t('require.number')),
  })

  const resolver = useYupValidationResolver(schema)

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormDataType>({
    defaultValues: {
      date: new Date(),
      greenVibrio: undefined,
      yellowVibrio: undefined,
    },
    resolver,
  })

  useEffect(() => {
    if (date) {
      reset((values) => ({
        ...values,
        date: date,
      }))
    }
  }, [date])

  useEffect(() => {
    if (vibrioEditData && isEdit) {
      reset((_val) => ({
        date: parseISO(vibrioEditData.date),
        greenVibrio: vibrioEditData.greenVibrio,
        yellowVibrio: vibrioEditData.yellowVibrio,
      }))
    }
  }, [vibrioEditData, isEdit])

  const onSuccess = (res: AxiosResponse) => {
    const { status } = res.data
    if (status === 200) {
      setSubmitting(false)
      onClose()
      reset()
      const normalizedSpecies = normalizeSpeciesName(speciesName)
      trackEvent(isEdit ? AnalyticsEvents.waterQualityRecordUpdated : AnalyticsEvents.waterQualityRecordCreated, {
        water_record_type: 'vibrio',
        ...(normalizedSpecies ? { species: normalizedSpecies } : {}),
      })
    }
  }

  const onError = (error: unknown, _variables: any, _context: unknown) => {
    const { response } = error as AxiosError
    if (response?.status === 400) {
      setError('date', {
        type: 'server',
        message: t('water:duplicate.record'),
      })
    }
  }

  const onSubmit = async (data: FormDataType) => {
    const formattedDate = formatDate(data.date)
    const greenVibrio = Number(data.greenVibrio)
    const yellowVibrio = Number(data.yellowVibrio)

    if (isEdit) {
      await updateWaterBacteriaMutation
        .mutateAsync(
          {
            vibrioId: Number(vibrioEditData?.id),
            body: {
              date: formattedDate,
              greenVibrio,
              yellowVibrio,
            },
          },
          { onSuccess, onError }
        )
        .finally(() => setSubmitting(false))
      return
    }
    await recordWaterBacteriaMutation
      .mutateAsync(
        {
          body: {
            date: formattedDate,
            greenVibrio,
            yellowVibrio,
          },
        },
        { onSuccess, onError }
      )
      .finally(() => setSubmitting(false))
  }

  return (
    <BaseRecordDialog
      onClose={onClose}
      open={open}
      formId="record-bacteria"
      isSubmitting={submitting}
      title={t('water:record.bacteria.title')}
      isShowPondName={false}
      enableCloseButton={true}
      submitButtonLabel={t('common:submit')}
    >
      <form id="record-bacteria" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Grid item xs={6}>
            <Box sx={{ paddingTop: 1, mb: 1 }}>
              <Typography fontSize={16} fontWeight={600}>
                <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                  *
                </Typography>
                {t('common:date')}
              </Typography>
            </Box>

            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <Stack direction="row" spacing={1} alignItems="center">
                  <ThaiDatePicker
                    {...field}
                    format="dd/MM/yyyy"
                    maxDate={endOfToday()}
                    slotProps={{
                      textField: {
                        id: 'bacteria-date-picker',
                        size: 'small',
                        error: !!errors.date,
                        helperText: errorMessageByField('date', errors),
                        fullWidth: true,
                      },
                    }}
                    readOnly={isEdit}
                  />
                </Stack>
              )}
            />
          </Grid>

          <Stack direction="row" gap={2}>
            {/* Green Vibrio */}
            <Stack spacing={1} flex={1}>
              <Typography color="primary.main" fontSize={16}>
                {t('water:green.vibrio')}
              </Typography>
              <TextFieldWithUnit
                name="greenVibrio"
                control={control}
                errors={errors}
                placeholder={t('common:input.amount')}
                unit="cfu/ml"
              />
            </Stack>

            {/* Yellow Vibrio */}
            <Stack spacing={1} flex={1}>
              <Typography color="#FFA940" fontSize={16}>
                {t('water:yellow.vibrio')}
              </Typography>

              <TextFieldWithUnit
                name="yellowVibrio"
                control={control}
                errors={errors}
                placeholder={t('common:input.amount')}
                unit="cfu/ml"
              />
            </Stack>
          </Stack>
        </Stack>
      </form>
    </BaseRecordDialog>
  )
}

export default BacteriaRecordDialog
